<script>
  // Quick-start docs panel for the webhook capture endpoint. Mounted at the
  // top of /captures (and surfaced from /buckets) so anyone landing on the
  // page can figure out the URL shape and a working curl in 30 seconds.
  // No need to read AGENTS.md or chase the Go source.
  import { ui } from '../lib/ui.svelte.js';

  let { bucketId = '' } = $props();
  let open = $state(false);

  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://hook.developer.telebroad.app';
  const globalUrl = `${origin}/h`;
  const bucketUrl = bucketId ? `${origin}/h/${bucketId}` : `${origin}/h/<bucket-id>`;

  function copy(text) {
    navigator.clipboard?.writeText(text);
    ui.snack('Copied', 'success', 1500);
  }

  const curlPost = `curl -X POST '${bucketId ? bucketUrl : globalUrl}/orders/created' \\
  -H 'Content-Type: application/json' \\
  -H 'X-Event: order.created' \\
  -d '{"order_id":42,"total":12.50}'`;

  const curlGet = `curl '${bucketId ? bucketUrl : globalUrl}/health?ping=1'`;

  // Plain-markdown version of the whole panel for one-click sharing in
  // Slack / docs / a ticket. Re-rendered when bucketId changes so the URLs
  // and curl examples in the copied text always match what the user sees.
  const fullDoc = $derived(
`# How to send a webhook

**Endpoint:** \`${bucketId ? bucketUrl : globalUrl}\`

## Endpoint
Any HTTP method. Any path. The body, headers, query string, and source IP are all stored — there's no fixed schema. The server always replies 200 OK so the sender treats the delivery as successful.

- POST, PUT, PATCH, GET, DELETE — all kept.
- Content type is recorded as-is (JSON, form, XML, raw bytes).
- Response headers: \`X-Capture-ID\` (the new doc's _id) and \`X-Bucket-ID\` (when routed to a bucket).
- Body is capped at \`CAPTURE_MAX_BODY_BYTES\` (1 MB by default); larger payloads are stored truncated with \`body_truncated: true\`.
- Captures TTL out after \`CAPTURE_RETENTION_DAYS\` (30 days by default).

## Buckets
Two URL shapes:

- **Global stream** — \`${globalUrl}/<anything>\`. Captures land in *global* with no bucket attached. Good for ad-hoc / one-off testing.
- **Bucket stream** — \`${origin}/h/<bucket-id>/<anything>\`. The first path segment is matched against existing bucket ids; if it matches, the capture is filed under that bucket and the rest of the path is recorded as the captured path. Create buckets on the Buckets tab.

The same URL handles both — there's no separate "create-on-receive" call. If the first segment doesn't match a known bucket id, the request just goes to global.

## Quick test (curl)

\`\`\`bash
${curlPost}
\`\`\`

\`\`\`bash
${curlGet}
\`\`\`

## What's captured
- \`method\` — request method.
- \`path\` — request path (with the bucket prefix stripped if there was one).
- \`query\` — raw query string.
- \`headers\` — full request headers, including any X-* custom ones.
- \`body\` — request body (truncated if it exceeded the cap; flagged via \`body_truncated\`; binary bodies are base64-encoded with \`body_base64: true\`).
- \`remote_addr\` — source IP.
- \`user_agent\`, \`content_type\`, \`subject\` (best-effort hint from X-Event-style headers).
- \`created_at\`, \`bucket_id\` (or empty for global).
`
  );
</script>

<section class="cap-help bg-surface-container rounded-2xl mb-4 overflow-hidden">
  <button
    type="button"
    class="cap-help-trigger"
    onclick={() => (open = !open)}
    aria-expanded={open}
  >
    <span class="material-symbols-outlined text-primary">help</span>
    <span class="font-medium">How to send a webhook</span>
    <code class="cap-help-url">{bucketId ? bucketUrl : globalUrl}</code>
    <span class="material-symbols-outlined text-on-surface-variant cap-help-chevron" class:open>expand_more</span>
  </button>

  {#if open}
    <div class="cap-help-body">
      <!-- One-click copy of the whole panel as markdown, for pasting into
           Slack / docs / a ticket. Sits at the top so it's the first thing
           you reach when the panel opens. -->
      <div class="cap-help-toolbar">
        <button
          type="button"
          class="cap-help-copyall"
          onclick={() => copy(fullDoc)}
          title="Copy entire doc (markdown)"
        >
          <span class="material-symbols-outlined text-base align-middle">content_copy</span>
          Copy whole doc
        </button>
      </div>

      <div class="cap-help-grid">
        <div>
          <h3>Endpoint</h3>
          <p>
            Any HTTP method. Any path. The body, headers, query string, and source IP are all stored — there's no fixed schema.
            The server always replies <code>200 OK</code> so the sender treats the delivery as successful.
          </p>
          <ul>
            <li><code>POST</code>, <code>PUT</code>, <code>PATCH</code>, <code>GET</code>, <code>DELETE</code> — all kept.</li>
            <li>Content type is recorded as-is (JSON, form, XML, raw bytes).</li>
            <li>Response headers: <code>X-Capture-ID</code> (the new doc's <code>_id</code>) and <code>X-Bucket-ID</code> (when routed to a bucket).</li>
            <li>Body is capped at the cluster's <code>CAPTURE_MAX_BODY_BYTES</code> (1 MB by default); larger payloads are stored truncated with <code>body_truncated: true</code>.</li>
            <li>Captures TTL out after <code>CAPTURE_RETENTION_DAYS</code> (30 days by default).</li>
          </ul>
        </div>

        <div>
          <h3>Buckets</h3>
          <p>
            Two URL shapes:
          </p>
          <ul>
            <li>
              <strong>Global stream</strong> — <code>{globalUrl}/<em>anything</em></code>.
              Captures land in <em>global</em> with no bucket attached. Good for ad-hoc / one-off testing.
            </li>
            <li>
              <strong>Bucket stream</strong> — <code>{origin}/h/<em>&lt;bucket-id&gt;</em>/<em>anything</em></code>.
              The first path segment is matched against existing bucket ids; if it matches, the capture is filed under that bucket and the rest of the path is recorded as the captured path. Create buckets on the
              <a href="/buckets" class="cap-help-link">Buckets</a> tab.
            </li>
          </ul>
          <p class="cap-help-note">
            The same URL handles both — there's no separate "create-on-receive" call. If the first segment doesn't match a known bucket id, the request just goes to global.
          </p>
        </div>
      </div>

      <h3 class="cap-help-h3-spaced">Quick test (curl)</h3>
      <div class="cap-help-codewrap">
        <pre><code>{curlPost}</code></pre>
        <button class="cap-help-copy" onclick={() => copy(curlPost)} title="Copy">
          <span class="material-symbols-outlined">content_copy</span>
        </button>
      </div>
      <div class="cap-help-codewrap">
        <pre><code>{curlGet}</code></pre>
        <button class="cap-help-copy" onclick={() => copy(curlGet)} title="Copy">
          <span class="material-symbols-outlined">content_copy</span>
        </button>
      </div>

      <h3 class="cap-help-h3-spaced">What's captured</h3>
      <ul>
        <li><code>method</code> — request method.</li>
        <li><code>path</code> — request path (with the bucket prefix stripped if there was one).</li>
        <li><code>query</code> — raw query string.</li>
        <li><code>headers</code> — full request headers, including any <code>X-*</code> custom ones.</li>
        <li><code>body</code> — request body (truncated if it exceeded the cap; flagged via <code>body_truncated</code>; binary bodies are base64-encoded with <code>body_base64: true</code>).</li>
        <li><code>remote_addr</code> — source IP.</li>
        <li><code>user_agent</code>, <code>content_type</code>, <code>subject</code> (best-effort hint from <code>X-Event</code>-style headers).</li>
        <li><code>created_at</code>, <code>bucket_id</code> (or empty for global).</li>
      </ul>
    </div>
  {/if}
</section>

<style>
  .cap-help-trigger {
    inline-size: 100%;
    display: grid;
    grid-template-columns: auto auto 1fr auto;
    gap: 0.75rem;
    align-items: center;
    padding: 0.875rem 1rem;
    background: transparent;
    color: inherit;
    border: 0;
    text-align: start;
    cursor: pointer;
  }
  .cap-help-trigger:hover { background: rgb(var(--md-sys-color-on-surface) / 0.04); }
  .cap-help-url {
    font-family: 'Menlo', 'Consolas', monospace;
    font-size: 12px;
    color: rgb(var(--md-sys-color-on-surface-variant));
    background: rgb(var(--md-sys-color-surface));
    padding: 4px 8px;
    border-radius: 6px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    justify-self: end;
  }
  .cap-help-chevron { transition: transform 200ms; }
  .cap-help-chevron.open { transform: rotate(180deg); }

  .cap-help-body {
    padding: 0 1rem 1rem;
    border-block-start: 1px solid rgb(var(--md-sys-color-outline-variant));
    color: rgb(var(--md-sys-color-on-surface));
  }
  .cap-help-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
    padding-block-start: 0.5rem;
  }

  .cap-help-toolbar {
    display: flex;
    justify-content: flex-end;
    padding-block-start: 0.75rem;
  }
  .cap-help-copyall {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.375rem 0.875rem;
    background: rgb(var(--md-sys-color-surface));
    color: rgb(var(--md-sys-color-on-surface));
    border: 1px solid rgb(var(--md-sys-color-outline-variant));
    border-radius: 999px;
    font-family: inherit;
    font-size: 12px;
    cursor: pointer;
  }
  .cap-help-copyall:hover { background: rgb(var(--md-sys-color-on-surface) / 0.08); }
  .cap-help-body h3 {
    font-size: 14px;
    font-weight: 500;
    margin-block: 0 0.5rem;
    color: rgb(var(--md-sys-color-on-surface));
  }
  .cap-help-h3-spaced { margin-block-start: 1.25rem; }
  .cap-help-body p {
    font-size: 14px;
    line-height: 1.55;
    color: rgb(var(--md-sys-color-on-surface-variant));
    margin-block: 0 0.5rem;
  }
  .cap-help-body ul {
    margin: 0;
    padding-inline-start: 1.1rem;
    font-size: 13px;
    line-height: 1.6;
    color: rgb(var(--md-sys-color-on-surface-variant));
  }
  .cap-help-body code {
    font-family: 'Menlo', 'Consolas', monospace;
    font-size: 12px;
    background: rgb(var(--md-sys-color-on-surface) / 0.06);
    padding: 1px 5px;
    border-radius: 4px;
  }
  .cap-help-link { color: rgb(var(--md-sys-color-primary)); text-decoration: underline; }
  .cap-help-note {
    margin-block-start: 0.5rem;
    font-size: 12px;
    font-style: italic;
  }

  .cap-help-codewrap {
    position: relative;
    margin-block-end: 0.5rem;
  }
  .cap-help-codewrap pre {
    background: rgb(var(--md-sys-color-surface));
    color: rgb(var(--md-sys-color-on-surface));
    padding: 0.75rem 2.75rem 0.75rem 0.75rem;
    border-radius: 8px;
    overflow-x: auto;
    margin: 0;
    font-size: 12px;
    line-height: 1.6;
    border: 1px solid rgb(var(--md-sys-color-outline-variant));
  }
  .cap-help-copy {
    position: absolute;
    inset-block-start: 6px;
    inset-inline-end: 6px;
    inline-size: 32px;
    block-size: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    color: rgb(var(--md-sys-color-on-surface-variant));
    border: 0;
    border-radius: 50%;
    cursor: pointer;
  }
  .cap-help-copy:hover { background: rgb(var(--md-sys-color-on-surface) / 0.08); }
  .cap-help-copy .material-symbols-outlined { font-size: 18px; }
</style>
